import React from 'react';
import SalesDetailPage from './SalesDetailPage';
import { TICKET_COLUMNS, SORT_TYPES, getDefaultVisibleColumns, getDefaultHiddenColumns } from '../constants/ticketsColumnsConfig';
import { buildSalesFilterGroups } from '../constants/salesFiltersConfig';
import { currencyFormatter, compactCurrency } from '../utils/reportingUtils';

const buildTicketsFilterGroups = (venueOwnerships, eventOptions, tagOptions, sellerOptions) =>
  buildSalesFilterGroups(venueOwnerships, eventOptions, tagOptions, { includeSources: true, sellerOptions });

const TICKETS_CONFIG = {
  pageTitle: 'Tickets',
  pageSubtitle: 'Ticket sales across your venues.',
  sidebarKey: 'sales_tickets',
  defaultPreset: 'this_month',

  columns: TICKET_COLUMNS,
  sortTypes: SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
  localStorageKey: (teamId) => `reportingSalesTicketColumns_${teamId}`,
  firstColumnId: 'order_number',

  salesDataKey: 'tickets',
  venueSeriesKey: 'tickets',
  detailEndpoint: (teamId) => `/teams/${teamId}/reporting/dashboard/sales-tickets/data.json`,
  detailResponseKey: 'orders',
  exportBasePath: (teamId) => `/teams/${teamId}/reporting/dashboard/sales-tickets/export`,
  salesExportSection: 'tickets',
  detailErrorMessage: 'Failed to load orders.',
  detailTableTitle: 'Orders',
  emptyMessage: 'No orders found.',
  searchPlaceholder: 'Search orders...',
  paginationLabel: 'orders',
  itemLabel: (count) => `order${count !== 1 ? 's' : ''}`,

  buildKpiCards: (current, prior) => [
    { label: 'Ticket Revenue', value: compactCurrency(current.revenue || 0), current: current.revenue || 0, prior: prior.revenue || 0 },
    { label: 'Tickets Sold', value: (current.count || 0).toLocaleString(), current: current.count || 0, prior: prior.count || 0 },
    { label: 'Unique Orders', value: (current.unique_orders || 0).toLocaleString(), current: current.unique_orders || 0, prior: prior.unique_orders || 0 },
  ],
  kpiGridColumns: 3,

  chartColor: 'var(--sl-fg-brand, #1982c4)',
  salesCardTitle: 'Tickets',
  buildMetricValue: (d) => currencyFormatter.format(d.revenue || 0),
  buildMetricLabel: (d) => `${(d.count || 0).toLocaleString()} tickets sold`,
  overTimeKey: 'over_time',
  countLabel: 'tickets',

  topCharts: [
    { title: 'Top 10 Events by Tickets Sold', dataKey: 'top_events_by_tickets', barDataKey: 'tickets_sold', barColor: 'var(--sl-fg-brand, #1982c4)', isCurrency: false, tooltipLabel: 'Tickets Sold' },
    { title: 'Top 10 Events by Revenue', dataKey: 'top_events_by_revenue', barDataKey: 'revenue', barColor: 'var(--sl-fg-brand, #1982c4)', isCurrency: true, tooltipLabel: 'Revenue' },
  ],

  groupByOptions: [
    { key: 'event', label: 'Event' },
    { key: 'order', label: 'Order' },
  ],
  groupByDefault: 'event',
  eventGroupedColumns: [
    { key: 'event_name', label: 'Event' },
    { key: 'venue_name', label: 'Venue' },
    { key: 'event_date', label: 'Event Date' },
    { key: 'order_count', label: 'Orders' },
    { key: 'total_tickets', label: 'Tickets' },
    { key: 'face_value', label: 'Face Value' },
    { key: 'ticket_fees', label: 'Ticket Fees' },
    { key: 'taxes', label: 'Taxes' },
    { key: 'total_revenue', label: 'Revenue' },
    { key: 'addon_revenue', label: 'Add-ons' },
    { key: 'donation_revenue', label: 'Donations' },
    { key: 'sold_today', label: 'Sold Today', defaultVisible: false },
    { key: 'sold_yesterday', label: 'Sold Yesterday', defaultVisible: false },
    { key: 'sold_last_7_days', label: 'Sold Last 7 Days', defaultVisible: false },
    { key: 'sold_last_14_days', label: 'Sold Last 14 Days', defaultVisible: false },
    { key: 'sold_last_30_days', label: 'Sold Last 30 Days', defaultVisible: false },
  ],

  renderEventCell: (key, row, { formatEventDate, currencyFormatter: cf }) => {
    switch (key) {
      case 'event_name': return row.event_name || '\u2014';
      case 'venue_name': return row.venue_name || '\u2014';
      case 'event_date': return row.event_date ? formatEventDate(row.event_date) : '\u2014';
      case 'order_count': return row.order_count.toLocaleString();
      case 'total_tickets': return row.total_tickets.toLocaleString();
      case 'face_value': return cf.format(row.face_value || 0);
      case 'ticket_fees': return cf.format(row.ticket_fees || 0);
      case 'taxes': return cf.format(row.taxes || 0);
      case 'total_revenue': return cf.format(row.total_revenue || 0);
      case 'addon_revenue': return cf.format(row.addon_revenue || 0);
      case 'donation_revenue': return cf.format(row.donation_revenue || 0);
      case 'sold_today':
      case 'sold_yesterday':
      case 'sold_last_7_days':
      case 'sold_last_14_days':
      case 'sold_last_30_days':
        return (row[key] || 0).toLocaleString();
      default: return '\u2014';
    }
  },

  renderCellValue: (col, order, { formatEventDate, formatEventDateTime, currencyFormatter: cf }) => {
    switch (col.id) {
      case 'order_number': return order.order_number;
      case 'event_name': return order.event_name || '\u2014';
      case 'customer_name': return order.customer_name || '\u2014';
      case 'customer_email': return order.customer_email || '\u2014';
      case 'amount': return cf.format(order.amount || 0);
      case 'ticket_qty': return (order.ticket_qty || 0).toLocaleString();
      case 'order_date': return <span className="text-nowrap">{formatEventDateTime(order.order_date)}</span>;
      case 'event_date': return <span className="text-nowrap">{order.event_date ? formatEventDate(order.event_date) : '\u2014'}</span>;
      case 'venue_name': return order.venue_name || '\u2014';
      case 'subtotal': return cf.format(order.subtotal || 0);
      case 'fees': return cf.format(order.fees || 0);
      case 'taxes': return cf.format(order.taxes || 0);
      case 'discount': return cf.format(order.discount || 0);
      case 'royalty': return cf.format(order.royalty || 0);
      case 'order_type': return order.order_type || '\u2014';
      case 'state': return order.state ? <span className="badge badge-secondary">{order.state}</span> : '\u2014';
      case 'complimentary': return order.complimentary ? 'Yes' : 'No';
      case 'cash': return order.cash ? 'Yes' : 'No';
      case 'promo_code': return order.promo_code || '\u2014';
      case 'sold_by': return order.sold_by || '\u2014';
      case 'device_name': return order.device_name || '\u2014';
      default: return '\u2014';
    }
  },

  filterTitle: 'Filter Tickets',
  filterSubtitle: 'Apply filters to narrow ticket sales data.',
  buildFilterGroups: buildTicketsFilterGroups,
};

function ReportingSalesTicketsPage(props) {
  return <SalesDetailPage {...props} config={TICKETS_CONFIG} />;
}

export default ReportingSalesTicketsPage;
